import { useEffect, useState } from "react";
import { useMyProviderDataClient } from "../../../../context/ApiContext";

export interface DemographicsResult {
  data?: any;
  finalization: boolean;
  loading: boolean;
  error?: string;
}

export const useDemographics = (
  username?: string,
  year?: number,
  quarter?: number,
  state?: string,
  enabled: boolean = true
): DemographicsResult => {
  const client = useMyProviderDataClient();

  const [data, setData] = useState<any>();
  const [finalization, setFinalization] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!enabled || !username) return;

    const load = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 1️⃣ Get EPR
        const eprRes = await client.get("/EmployeeInformation/EPR", {
          params: { username, year, quarter }
        });

        const epr = eprRes.data;

        if (!epr) {
          setData(undefined);
          return;
        }

        // 2️⃣ Build base demographics object
        const demographics: any = {
          status: epr.status,
          role: epr.role,
          region: epr.region,
          tier: 0,
          hoursRequired: epr.hoursRequired,
          totalHoursRequired: epr.totalHoursRequired,
          bonusIneligible: epr.bonusIneligible,
          salary: epr.annualPay,
          analystLevel: epr.analystLevel,
          startDate: epr.startDate,
          rehireDate: epr.rehireDate,
          statusChange: epr.statusChange,
          anniversaryDate: epr.anniversaryDate
        };

        // 3️⃣ Check proposed contract changes (Finalization flag)
        const peccRes = await client.get(
          "/EmployeeInformation/ProposedEmployeeContractChanges",
          { params: { username } }
        );

        const pecc = peccRes.data;

        if (pecc?.effectiveDate) {
          const effective = new Date(pecc.effectiveDate);
          const today = new Date();

          const diff =
            (today.getTime() - effective.getTime()) /
            (1000 * 3600 * 24);

          if (diff <= 8 && diff >= 0) {
            setFinalization(true);
          }
        }

        // 4️⃣ If Exempt Analyst → fetch Tier
        if (
          demographics.status === "Exempt" &&
          demographics.analystLevel
        ) {
          const tierRes = await client.get("/EmployeeInformation/ExemptAnalystTier", {
            params: {
              hoursRequired: demographics.hoursRequired,
              analystLevel: demographics.analystLevel,
              state
            }
          });

          demographics.tier = tierRes.data?.[0]?.tier ?? 0;
        }

        setData(demographics);
      } catch (err) {
        console.error(err);
        setError("Failed to load employee data.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [username, year, quarter, state, enabled, client]);

  return { data, finalization, loading, error };
};